import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { UiActions } from 'modules/ui';
import { AccountActions } from 'modules/account';
import {
  YEAR_OF_BIRTH_DEFAULT,
  MONTH_OF_BIRTH_DEFAULT,
  JAPAN_PREFECTURE_ID_DEFAULT,
  PHONE_COUNTRY_ID_DEFAULT,
  ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
  ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
  ADDRESS_OVERSEAS_ID,
} from 'constants/profile';
import { NO_SELECT_NUMBER } from 'constants/form';
import apiInnovator from 'external/api/innovator';
import { UpdateInnovatorRequest } from 'proto/v1/innovatorservice/innovatorservice';
import { RegisterProfileFormNewGraduateFirstValues } from 'types/profile';
import {
  getDefaultPhoneCountryIdValue,
  getDefaultAcademicBackgroundSchoolValue,
  getDefaultNewGraduateResearchRoomsValue,
} from 'utils/profile/defaultFormValues';
import {
  getPhoneCountryCodeValueToUpdate,
  getJapanPrefectureValueToUpdate,
  getNewGraduateResearchRoomsValueToUpdate,
} from 'utils/profile/formValuesToUpdate';
import { scrollLayoutBodyToTop } from 'utils/layout';
import {
  lastNameSchema,
  firstNameSchema,
  lastNameKanaSchema,
  firstNameKanaSchema,
  yearOfBirthSchema,
  monthOfBirthSchema,
  phoneNumberSchema,
  phoneCountryIdSchema,
  japanPrefectureIdSchema,
  academicBackgroundSchema,
  newGraduateResearchRoomsSchema,
} from 'utils/profile/schema';
import RequestError from 'classes/RequestError';
import { Stepper } from 'hooks/useStepper';
import useErrorNotification from 'hooks/useErrorNotification';
import useFetchInnovatorForm from 'hooks/profile/useFetchInnovatorForm';
import useAcademicBackgroundSchoolFormField from 'hooks/profile/useAcademicBackgroundSchoolFormField';
import useAutoCompleteKanaName from 'hooks/profile/useAutoCompleteKanaName';
import { stepsNewGraduate } from '../../steps';

const schema = object().shape({
  lastName: lastNameSchema,
  firstName: firstNameSchema,
  lastNameKana: lastNameKanaSchema,
  firstNameKana: firstNameKanaSchema,
  yearOfBirth: yearOfBirthSchema,
  monthOfBirth: monthOfBirthSchema,
  phoneNumber: phoneNumberSchema,
  phoneCountryId: phoneCountryIdSchema,
  japanPrefectureId: japanPrefectureIdSchema,
  academicBackground: academicBackgroundSchema,
  newGraduate: object().shape({
    researchRooms: newGraduateResearchRoomsSchema,
  }),
});

const defaultValues: RegisterProfileFormNewGraduateFirstValues = {
  lastName: '',
  firstName: '',
  lastNameKana: '',
  firstNameKana: '',
  yearOfBirth: YEAR_OF_BIRTH_DEFAULT,
  monthOfBirth: MONTH_OF_BIRTH_DEFAULT,
  phoneNumber: '',
  phoneCountryId: PHONE_COUNTRY_ID_DEFAULT,
  japanPrefectureId: JAPAN_PREFECTURE_ID_DEFAULT,
  academicBackground: {
    school: null,
    faculty: '',
    departmentId: NO_SELECT_NUMBER,
    yearOfGraduation: ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
    monthOfGraduation: ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
  },
  newGraduate: {
    researchRooms: [],
  },
};

const useRegisterProfileFormNewGraduateFirst = ({
  stepper,
}: {
  stepper: Stepper;
}) => {
  const dispatch = useDispatch();
  const { handleRequestError } = useErrorNotification();
  const { formItems, innovator, innovatorToUpdate } = useFetchInnovatorForm();

  const [isValidDefault, setIsValidDefault] = useState<boolean>(false);

  const onSubmit = useCallback(
    async (values: RegisterProfileFormNewGraduateFirstValues) => {
      const valuesToUpdate: UpdateInnovatorRequest = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...innovatorToUpdate!,
        lastName: values.lastName,
        firstName: values.firstName,
        lastNameKana: values.lastNameKana,
        firstNameKana: values.firstNameKana,
        yearOfBirth: values.yearOfBirth,
        monthOfBirth: values.monthOfBirth,
        phoneNumber: values.phoneNumber,
        phoneCountryCode: getPhoneCountryCodeValueToUpdate(
          values.phoneCountryId,
          formItems.countries,
        ),
        japanPrefectureId: getJapanPrefectureValueToUpdate(
          values.japanPrefectureId,
        ),
        academicBackground: {
          schoolId: values.academicBackground.school?.value ?? NO_SELECT_NUMBER,
          faculty: values.academicBackground?.faculty ?? '',
          departmentId:
            values.academicBackground?.departmentId ?? NO_SELECT_NUMBER,
          yearOfGraduation:
            values.academicBackground?.yearOfGraduation ??
            ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
          monthOfGraduation:
            values.academicBackground?.monthOfGraduation ??
            ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
        },
        newGraduate: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ...innovatorToUpdate!.newGraduate!,
          researchRooms: getNewGraduateResearchRoomsValueToUpdate(
            values.newGraduate.researchRooms,
          ),
        },
      };
      let data;
      try {
        ({ data } = await apiInnovator.updateInnovator(valuesToUpdate));
      } catch (error: unknown) {
        if (error instanceof RequestError) {
          dispatch(UiActions.setLoading(false));
          handleRequestError(error);
        }
        return;
      }
      dispatch(AccountActions.setInnovator(data.innovator));
      dispatch(UiActions.setLoading(false));
      scrollLayoutBodyToTop();
      stepsNewGraduate.formFirst.completed = true;
      stepper.handleNext();
    },
    [
      dispatch,
      formItems.countries,
      handleRequestError,
      innovatorToUpdate,
      stepper,
    ],
  );

  const formMethods = useForm<RegisterProfileFormNewGraduateFirstValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { reset } = formMethods;

  useAutoCompleteKanaName(formMethods);

  const {
    schoolOptionsState,
    handleInputChangeSchool,
    handleCreateSchoolOption,
    fetchMoreSchoolOptions,
  } = useAcademicBackgroundSchoolFormField({ formMethods });

  // Set default values by the fetched innovator
  useEffect(() => {
    if (!innovator || !innovatorToUpdate) return;

    const values: RegisterProfileFormNewGraduateFirstValues = {
      lastName: innovatorToUpdate.lastName,
      firstName: innovatorToUpdate.firstName,
      lastNameKana: innovatorToUpdate.lastNameKana,
      firstNameKana: innovatorToUpdate.firstNameKana,
      yearOfBirth: innovatorToUpdate.yearOfBirth,
      monthOfBirth: innovatorToUpdate.monthOfBirth,
      phoneNumber: innovatorToUpdate.phoneNumber,
      phoneCountryId: getDefaultPhoneCountryIdValue(
        innovatorToUpdate.phoneCountryCode,
        formItems.countries,
      ),
      japanPrefectureId:
        innovatorToUpdate.japanPrefectureId ?? ADDRESS_OVERSEAS_ID,
      academicBackground: {
        school: getDefaultAcademicBackgroundSchoolValue(
          innovator.academicBackground?.school,
        ),
        faculty: innovatorToUpdate.academicBackground?.faculty ?? '',
        departmentId:
          innovatorToUpdate.academicBackground?.departmentId ??
          NO_SELECT_NUMBER,
        yearOfGraduation:
          innovatorToUpdate.academicBackground?.yearOfGraduation ??
          ACADEMIC_BACKGROUND_YEAR_OF_GRADUATION_DEFAULT,
        monthOfGraduation:
          innovatorToUpdate.academicBackground?.monthOfGraduation ??
          ACADEMIC_BACKGROUND_MONTH_OF_GRADUATION_DEFAULT,
      },
      newGraduate: {
        researchRooms: getDefaultNewGraduateResearchRoomsValue(
          innovatorToUpdate.newGraduate?.researchRooms,
        ),
      },
    };

    setIsValidDefault(schema.isValidSync(values));

    reset(values);
  }, [formItems.countries, innovator, innovatorToUpdate, reset]);

  return {
    formMethods,
    formItems,
    onSubmit,
    schoolOptionsState,
    isValidDefault,
    handleInputChangeSchool,
    handleCreateSchoolOption,
    fetchMoreSchoolOptions,
  };
};

export default useRegisterProfileFormNewGraduateFirst;
